import { confirmationPaneName } from '@pequity/shared/components/confirmation-pane/constants';
import { type ConfirmationPaneOptions } from '@pequity/shared/components/confirmation-pane/types';
import { useSdfFocusPane } from '@pequity/shared/ui';
import { isComponent } from '@pequity/shared/utils/component';
import { markRaw, ref } from 'vue';
import { useI18n } from 'vue-i18n';

const defaults: Required<ConfirmationPaneOptions> = {
  type: 'default',
  title: '',
  body: '',
  bodyProps: {},
  confirmButtonText: '',
  cancelButtonText: '',
  preventClose: false,
  onConfirm: () => {},
  onCancel: () => {},
  onBeforeOpen: () => {},
  onClosed: () => {},
};

const options = ref<Required<ConfirmationPaneOptions>>({ ...defaults });
const confirmationPaneErrorMsg = ref('');

export const useConfirmationPane = () => {
  const { t } = useI18n();
  const isLoading = ref(false);
  const pane = useSdfFocusPane();

  const getDefaults = (): Required<ConfirmationPaneOptions> => ({
    ...defaults,
    title: t('modal_confirmation.title'),
    body: t('modal_confirmation.body'),
    confirmButtonText: t('app.confirm'),
    cancelButtonText: t('app.cancel'),
  });

  const onBeforeOpen = () => {
    options.value.onBeforeOpen?.();
  };

  const onClosed = () => {
    options.value.onClosed?.();
    options.value = getDefaults();
    isLoading.value = false;
    confirmationPaneErrorMsg.value = '';
  };

  const showConfirmationPane = (paneOptions?: ConfirmationPaneOptions) => {
    // Mark body as raw to prevent Vue from converting it to a reactive object
    if (isComponent(paneOptions?.body)) {
      paneOptions.body = markRaw(paneOptions.body);
    }

    options.value = { ...getDefaults(), ...paneOptions };
    pane.show(confirmationPaneName);
  };

  const hideConfirmationPane = () => {
    pane.hide(confirmationPaneName);
  };

  const onConfirm = async () => {
    const onConfirm = options.value?.onConfirm;

    // Set isLoading only for async functions
    if (onConfirm && onConfirm.constructor.name === 'AsyncFunction') {
      isLoading.value = true;
    }

    await onConfirm?.();

    // Show error message if any and stop loading
    if (confirmationPaneErrorMsg.value) {
      isLoading.value = false;
      return;
    }

    isLoading.value = false;
    hideConfirmationPane();
  };

  const onCancel = async () => {
    await options.value?.onCancel?.();

    hideConfirmationPane();
  };

  return {
    confirmationPaneOptions: options,
    confirmationPaneErrorMsg,
    isLoading,
    showConfirmationPane,
    hideConfirmationPane,
    onConfirm,
    onCancel,
    onBeforeOpen,
    onClosed,
  };
};
